import React, { useRef, useState, useEffect } from 'react';
import { UploadCloud, Info, Image as ImageIcon, Trash2 } from 'lucide-react';

export const ImageUploadBox = ({ label, hint, value, onChange, onUpload, type, accept = "image/png, image/webp, image/jpeg" }) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isError, setIsError] = useState(false);
  const dragCounter = useRef(0);

  useEffect(() => {
    setIsError(false);
  }, [value]);

  const handleFile = async (file) => {
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      onChange(previewUrl);
      if (onUpload) {
        try {
          await onUpload(type, file, type.split('_')[1] || type);
        } catch (err) {
          onChange('');
        }
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) handleFile(file);
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'copy'; };
  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); dragCounter.current++; if (e.dataTransfer.items && e.dataTransfer.items.length > 0) setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); dragCounter.current--; if (dragCounter.current === 0) setIsDragging(false); };
  const handleDrop = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); dragCounter.current = 0; const file = e.dataTransfer.files[0]; if (file) handleFile(file); };
  const handleClick = () => fileInputRef.current?.click();

  const isCollapsed = type === 'logo_collapsed';

  return (
    <div className={`flex flex-col gap-4 ${isCollapsed ? 'w-[120px]' : 'flex-1'}`}>
      <span className="font-semibold text-gray-800 dark:text-gray-200 text-center text-sm">{label}</span>
      <div 
        className={`relative border-2 border-dashed rounded-xl flex flex-col items-center justify-center transition-all cursor-pointer h-[120px] ${
          isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/10' : 'border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500'
        } ${isCollapsed ? 'w-[120px]' : 'w-full'}`}
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input type="file" className="hidden" ref={fileInputRef} onChange={handleFileChange} accept={accept} />
        
        {value ? (
          <div className="relative group flex items-center justify-center w-full h-full p-2">
            <img 
              src={value} 
              alt="Preview" 
              className={`max-h-full max-w-full object-contain ${isError ? 'hidden' : 'block'}`} 
              onError={(e) => {
                console.error("Failed to load image from URL:", value);
                setIsError(true);
              }}
            />
            {isError && (
              <div className="flex flex-col items-center justify-center text-center p-2">
                <span className="text-red-500 text-xs font-semibold mb-1">Upload Successful, but image is inaccessible (403/404)</span>
                <span className="text-gray-400 text-[9px] break-all max-w-full overflow-hidden leading-tight">{value}</span>
              </div>
            )}
            <button 
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              className="absolute -top-2 -right-2 p-1 bg-red-50 text-red-500 rounded-full shadow-sm hover:bg-red-100 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center text-center gap-1 p-2">
            <UploadCloud className={`${isCollapsed ? 'w-5 h-5' : 'w-6 h-6'} text-gray-400`} />
            <div className="flex flex-col gap-0.5">
               <p className={`${isCollapsed ? 'text-[10px]' : 'text-[12px]'} text-gray-600 dark:text-gray-400 leading-tight`}>
                 <span className="text-blue-600 font-semibold">{isCollapsed ? 'Upload' : 'Click to upload'}</span>
                 {!isCollapsed && ' or drag'}
               </p>
               <p className={`${isCollapsed ? 'text-[9px]' : 'text-[10px]'} text-gray-400`}>{hint.replace(' recommended', '')}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function BrandingVisualsTab({ form, update, onUpload }) {
  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      
      <div>
        <h3 className="font-bold text-gray-900 dark:text-white text-xl">Logos</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Recommended format: PNG/WebP/JPG, 140x80px (Light/Dark) & 80x80px (Collapsed)</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <ImageUploadBox 
          label="Logo (Light)" 
          hint="PNG/WebP/JPG: 140x80px recommended"
          value={form.logo_light_url}
          onChange={(val) => update('logo_light_url', val)}
          onUpload={(type, file) => onUpload('logo_light_url', file, 'light')}
          type="logo_light"
        />
        <ImageUploadBox 
          label="Logo (Dark)" 
          hint="PNG/WebP/JPG: 140x80px recommended"
          value={form.logo_dark_url}
          onChange={(val) => update('logo_dark_url', val)}
          onUpload={(type, file) => onUpload('logo_dark_url', file, 'dark')}
          type="logo_dark"
        />
        <ImageUploadBox 
          label="Collapsed/Icon Logo" 
          hint="PNG/WebP/JPG: 80x80px recommended"
          value={form.collapsed_logo_url}
          onChange={(val) => update('collapsed_logo_url', val)}
          onUpload={(type, file) => onUpload('collapsed_logo_url', file, 'collapsed')}
          type="logo_collapsed"
        />
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50 rounded-xl p-6">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="space-y-3">
            <h4 className="font-bold text-blue-900 dark:text-blue-300 text-sm flex items-center gap-2">
              Branding Image Guidelines
            </h4>
            <ul className="text-xs text-blue-800 dark:text-blue-400 space-y-2 leading-relaxed opacity-90">
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-blue-400 flex-shrink-0" />
                <span>Use <strong>PNG, WebP, or JPG</strong> for best platform compatibility.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-blue-400 flex-shrink-0" />
                <span>Images not matching the recommended dimensions will show a <strong>warning</strong> but can still be used.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-blue-400 flex-shrink-0" />
                <span>Maximum file size: <strong>2MB (logos)</strong>.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
